import React from 'react';

import { apply } from 'json-logic-js/logic';
import uuid from 'uuid/v4';

import { Alignment, DynamicFieldsType } from '@philips-emr/tws-constants';
import { propagateProps } from '@philips-emr/tws-utils';

import { Autocomplete, Checkbox, DateField, Field, Fieldset, Input, Mapper, NotFound, RadioGroup, Text, TextArea } from '..';

export function jsonConditionLogicEval(condition, data) {
  if (!data) {
    return apply(condition);
  }

  return apply(condition, data);
}

export function jsonLogicEval(formula, data) {
  if (!data) {
    return apply(formula);
  }

  return apply(formula, getFormulaValues(data));
}

function getInput(metaData) {
  const props = {
    ...metaData,
    alignment: metaData.alignment ? metaData.alignment.toLowerCase() : Alignment.CENTER,
    enabled: !metaData.readOnly,
    formatters: metaData.formatters,
  };

  return internalPropagateProps(<Input />, props);
}

function getCheckbox(metaData) {
  const props = {
    ...metaData,
    enabled: !metaData.readOnly,
    name: `${metaData.id}`,
  };

  return internalPropagateProps(<Checkbox />, props);
}

function getDateField(metaData) {
  const component = <DateField enabled={!metaData.readOnly} />;

  return internalPropagateProps(component, metaData);
}

function getAutocomplete(metaData) {
  const { enabled, label, minSearchLength, options } = metaData;
  const props = {
    ...metaData,
    enabled: enabled,
    entry: 'label',
    minSearchLength: minSearchLength ? minSearchLength : 0,
    title: label,
    uuid: uuid(),
  };

  const map = {
    value: 'label',
  };

  const component = (
    <Autocomplete data={options} >
      <Mapper map={map}>
        <Text />
      </Mapper>
    </Autocomplete>
  );

  return internalPropagateProps(component, props);
}

function getRadioGroup(metaData, onBlur, onFocus) {
  const { id, options, readOnly } = metaData;

  const map = {
    key: 'value',
    label: 'label',
    value: 'value',
  };

  const component = (
    <Mapper data={options} map={map}>
      <RadioGroup enabled={!readOnly} name={id} onBlur={onBlur} onFocus={onFocus} readOnly />
    </Mapper>
  );

  return component;
}

function getText(metaData) {
  const props = {
    ...metaData,
    alignment: metaData.alignment ? metaData.alignment.toLowerCase() : Alignment.CENTER,
    size: metaData.fontSize,
    value: metaData.label,
  };

  return internalPropagateProps(<Text />, props);
}

function getTextArea(metaData) {
  const props = {
    ...metaData,
    enabled: !metaData.readOnly,
    name: metaData.id,
  };

  return internalPropagateProps(<TextArea />, props);
}

function hideLabel(child) {
  return [DynamicFieldsType.TEXT].includes(child.fieldType.toUpperCase());
}

function getLabel(child) {
  return [DynamicFieldsType.TEXT, DynamicFieldsType.CHECKBOX].includes(child.fieldType.toUpperCase()) ? '' : child.label;
}

function getValidators(child) {
  const required = {
    name: 'required',
  };

  return child.required ? [required] : [];
}

function getComponent(child, addFormula, addVisualData, onBlur, onFocus) {
  const fieldType = child.fieldType.toUpperCase();

  const components = {
    [DynamicFieldsType.AUTOCOMPLETE]: getAutocomplete(child),
    [DynamicFieldsType.CHECKBOX]: getCheckbox(child),
    [DynamicFieldsType.DATE_FIELD]: getDateField(child),
    [DynamicFieldsType.INPUT]: getInput(child),
    [DynamicFieldsType.RADIO_GROUP]: getRadioGroup(child, onBlur, onFocus),
    [DynamicFieldsType.TEXT]: getText(child),
    [DynamicFieldsType.TEXT_AREA]: getTextArea(child),
  };

  const component = components[fieldType] || <Field />;

  if (fieldType === DynamicFieldsType.TEXT) {
    addVisualData(child.entry, child.label);
  }

  if (child.formula) {
    addFormula(child.entry, child.formula);
  }

  return component;
}

function getField(child, addFormula, addVisualData, onBlur, onFocus, visible) {
  const { entry, id, width } = child;
  return (
    <Field
      entry={`${entry}`}
      hideLabel={hideLabel(child)}
      key={`${id}`}
      label={getLabel(child)}
      validators={getValidators(child)}
      visible={visible}
      width={width}
    >
      {getComponent(child, addFormula, addVisualData, onBlur, onFocus)}
    </Field>
  );
}

function getFieldset(children, id, label, addFormula, addVisualData, onBlur, onFocus, visible) {
  return (
    <Fieldset title={label} collapsible key={`${id}`} >
      {children.map(child => getComponents(child, addFormula, addVisualData, onBlur, onFocus, visible))}
    </Fieldset>
  );
}

export function getComponents(child, addFormula, addVisualData, onBlur, onFocus, visible) {
  const { children, componentType, id, label } = child;

  if (children && componentType === DynamicFieldsType.FIELD_SET) {
    return getFieldset(children, id, label, addFormula, addVisualData, onBlur, onFocus, visible);
  }

  if (componentType === DynamicFieldsType.FIELD) {
    return getField(child, addFormula, addVisualData, onBlur, onFocus, visible);
  }

  return (
    <NotFound />
  );
}

function internalPropagateProps(component, props) {
  if (props.children) {
    delete props.children;
  }

  return propagateProps(component, props);
}

function getFormulaValues(data) {
  const result = {};
  for (const key in data) {
    const value = (data[key] && data[key].value) || data[key];
    result[key] = parseFloat(value) || 0;
  }

  return result;
}
